import { useNavigate } from 'react-router';
import { TSalesDataUploadResp } from 'pages/SalesData/types';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    Textarea,
    chakra,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Progress,
} from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { useEffect, useRef, useState } from 'react';
import { EXCEL_FILE_TYPE, GENERIC_EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { useTranslation } from 'helpers/languages/translations';
import { useLazyDownloadSalesDataTemplateQuery, useUploadSalesDataMutation } from 'core/API/SalesDataApi';
import { isErrorWithMessage, isFetchBaseQueryError } from 'utils/erros';
import FileSaver from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { linkStyle } from 'shared/styles';
import pusher from '../../../pusher';
import { useGetUserSelector } from 'core/redux/selectors/authSelectors';
import DescriptionHtml from 'components/molecules/DescriptionHtml';

const decodeArrayBufferJson = (buffer?: ArrayBuffer) => {
    if (!buffer) return null;

    try {
        const text = new TextDecoder().decode(buffer);
        return JSON.parse(text) as Record<string, any>;
    } catch {
        return null;
    }
};

type TSalesDataUploadProgressEvent = {
    progress: number;
    step: string;
    uploadId?: string;
    error?: string;
};

const SalesDataUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadSalesData, { isLoading }] = useUploadSalesDataMutation({
        fixedCacheKey: 'sales-data-upload-key',
    });
    const [downloadTemplate] = useLazyDownloadSalesDataTemplateQuery();
    const [errorFileBuffer, setErrorFileBuffer] = useState<ArrayBuffer | null>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const modalInitialRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadProgressStep, setUploadProgressStep] = useState('queued');
    const currentUploadIdRef = useRef<string | null>(null);
    const lastRealtimeProgressAtRef = useRef<number>(0);
    const userInfo = useGetUserSelector();
    const username = userInfo?.username || userInfo?.original_user;

    const [comment, setComment] = useState('');

    const navigate = useNavigate();
    const translate = useTranslation();
    const goBack = () => {
        navigate(-1);
    };
    const toast = useToast();

    useEffect(() => {
        if (!username) return;

        const channel = pusher.subscribe(`private-${username}`);
        const handleProgress = (event: TSalesDataUploadProgressEvent) => {
            const currentUploadId = currentUploadIdRef.current;
            if (!currentUploadId) return;
            if (event?.uploadId && event.uploadId !== currentUploadId) return;

            lastRealtimeProgressAtRef.current = Date.now();
            setUploadProgress(Math.max(0, Math.min(100, Number(event.progress) || 0)));
            setUploadProgressStep(event.step || 'processing');
        };

        channel.bind('sales-data-upload-progress', handleProgress);

        return () => {
            channel.unbind('sales-data-upload-progress', handleProgress);
            channel.unsubscribe();
        };
    }, [username]);

    useEffect(() => {
        if (!isLoading) return;

        const interval = window.setInterval(() => {
            const hasRecentRealtimeEvent = Date.now() - lastRealtimeProgressAtRef.current < 2000;
            if (hasRecentRealtimeEvent) return;

            setUploadProgress((previous) => {
                if (previous >= 95) return previous;
                return previous + 1;
            });
            setUploadProgressStep((previousStep) => (previousStep === 'queued' ? 'processing' : previousStep));
        }, 700);

        return () => {
            window.clearInterval(interval);
        };
    }, [isLoading]);
    const handleChangeFile = (file: File | null) => {
        if (GENERIC_EXCEL_FILE_TYPE.includes(file?.type as string)) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const { data: res, isError: isExportError, error } = await downloadTemplate();

            if (isExportError || !res) {
                if (isFetchBaseQueryError(error)) {
                    const errorMsg = error.data as string;
                    throw new Error(errorMsg);
                }

                throw new Error(translate('sales_data.upload.error.downloadTemplate') as string);
            }

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('sales_data.upload.templateFileName') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleDownloadErrorFile = async () => {
        try {
            if (!errorFileBuffer) {
                throw new Error(translate('error.export') as string);
            }

            const blob = new Blob([errorFileBuffer], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('sales_data.export.error_file') as string);
        } catch (error) {
            toast({
                title: (error as Error).message || translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderModal = () => {
        const title = translate('sales_data.upload.modal.error.title');

        const bodyContent = (
            <Text>
                {translate('page.sales_data.errorsUploadedDescription', {
                    downloadComp: (
                        <chakra.span onClick={handleDownloadErrorFile} {...linkStyle}>
                            {translate('page.sales_data.errorDownload')}
                        </chakra.span>
                    ),
                })}
            </Text>
        );

        return (
            <Modal isCentered initialFocusRef={modalInitialRef} isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={modalInitialRef}>
                        <Text as="h3" color={'error'}>
                            {title}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={isLoading} />
                    <ModalBody fontSize="sm">{bodyContent}</ModalBody>

                    <ModalFooter>
                        <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                            {translate('keyword.upload.modal.cancel')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const handleUploadSalesData = async () => {
        try {
            const uploadId = uuidv4();
            currentUploadIdRef.current = uploadId;
            lastRealtimeProgressAtRef.current = 0;
            setUploadProgress(1);
            setUploadProgressStep('queued');

            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            if (comment) bodyFormData.append('comment', comment);
            bodyFormData.append('uploadId', uploadId);
            const res = (await uploadSalesData({ data: bodyFormData, uploadId })) as TSalesDataUploadResp;
            if (res.error) {
                const errorPayload = decodeArrayBufferJson(res.error.data);
                const errorType = errorPayload?.error;

                if (errorPayload?.code === 'LIMIT_FILE_SIZE') throw translate('error.upload.file-size-limit');
                if (errorType === 'invalid_data') {
                    throw translate('error.generic');
                }

                if (!errorPayload && res.error.data) {
                    setErrorFileBuffer(res.error.data);
                    onOpen();
                    setUploadProgress(100);
                    setUploadProgressStep('failed');
                    return;
                }

                if (errorType === 'missing_headers') {
                    const missingHeaders = (errorPayload?.data as string[])?.join(', ');
                    setUploadProgress(100);
                    setUploadProgressStep('failed');
                    throw translate('sales_data.upload.error.missingHeaders', { missingHeaders });
                } else throw translate('error.generic');
            } else {
                const successPayload = decodeArrayBufferJson(res.data);
                if (!successPayload?.message) {
                    throw translate('error.generic');
                }

                setUploadProgress(100);
                setUploadProgressStep('completed');
                toast({
                    title: translate('sales_data.uplaod.message.success'),
                    status: 'success',
                    isClosable: true,
                });
                await new Promise<void>((resolve) => setTimeout(resolve, 1500));
                goBack();
            }
        } catch (error) {
            setUploadProgress(100);
            setUploadProgressStep('failed');
            toast({
                title: isErrorWithMessage(error)
                    ? error.message
                    : typeof error === 'string'
                        ? error
                        : translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleUploadChecker = async () => {
        await handleUploadSalesData();
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text color="gray.500">
                    <chakra.span fontWeight="700">{translate('keyword.upload.tip')}</chakra.span>
                    <chakra.span
                        {...linkStyle}
                        mx={1}
                        onClick={intentionalyFloatingPromiseReturn(handleDownloadTemplate)}
                    >
                        {translate('keyword.upload.templateBtn')}
                    </chakra.span>
                    {translate('keyword.upload.startingPoint')}
                </Text>

                <Text fontSize="md" pt={4} mb={2.5}>
                    {translate('sales_data.upload.comment')}
                </Text>
                <Textarea
                    placeholder={translate('sales_data.upload.comment.placeholder') as string}
                    resize="none"
                    background="white"
                    fontSize="md"
                    value={comment}
                    onChange={(e) => {
                        setComment(e.target.value);
                    }}
                />
                <Text fontSize="md" pt={4} pb={3}>
                    {translate('sales_data.upload.file')}
                </Text>

                <Box py={4} pt={0}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>

                {(isLoading || uploadProgress > 0) && (
                    <Box mt={4}>
                        <Progress value={uploadProgress} size="sm" borderRadius="md" colorScheme="blue" />
                        <Text mt={2} fontSize="sm" color="gray.600">
                            {`${translate('upload.progress.text')}: ${uploadProgress}% (${translate(
                                `sales_data.status.${uploadProgressStep}`,
                            )})`}
                        </Text>
                    </Box>
                )}
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        isLoading={isLoading}
                        onClick={intentionalyFloatingPromiseReturn(handleUploadChecker)}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('sales_data.upload.apply')}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate('sales_data.upload.title') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            {renderModal()}
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('sales_data.upload.header')}
                    </Heading>
                </Flex>
                <Flex fontSize="sm" direction="row" gap="32px">
                    {renderDescriptionSection()}
                    <DescriptionHtml h="fit-content" tradKey="sales_data.upload.description" />
                </Flex>
            </Box>
        </Box>
    );
};

export default SalesDataUpload;
